<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const providers = ref([]);
const cargando = ref(true);
const guardando = ref(false);

const showModal = ref(false);
const isEdit = ref(false);
const form = ref({ id: null, name: "", phone: "", email: "", notes: "" });

const showDetalle = ref(false);
const detalle = ref(null);
const nuevoServicio = ref({ name: "", price: "" });
const nuevoContacto = ref({ name: "", phone: "", role: "" });

async function getProviders() {
  const res = await api.get("/providers");
  providers.value = res.data.providers;
}

function resetForm() {
  form.value = { id: null, name: "", phone: "", email: "", notes: "" };
}

function abrirCrear() {
  resetForm();
  isEdit.value = false;
  showModal.value = true;
}

function abrirEditar(provider) {
  form.value = { id: provider.id, name: provider.name, phone: provider.phone || "", email: provider.email || "", notes: provider.notes || "" };
  isEdit.value = true;
  showModal.value = true;
}

async function guardarProvider() {
  if (!form.value.name) {
    Swal.fire({ icon: "warning", title: "El nombre es obligatorio" });
    return;
  }

  guardando.value = true;

  try {
    if (isEdit.value) {
      await api.put(`/providers/${form.value.id}`, form.value);
    } else {
      await api.post("/providers", form.value);
    }

    showModal.value = false;
    await getProviders();

    Swal.fire({ icon: "success", title: isEdit.value ? "Proveedor actualizado" : "Proveedor creado", timer: 1500, showConfirmButton: false });

  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo guardar el proveedor", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    guardando.value = false;
  }
}

async function eliminarProvider(provider) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: `¿Eliminar ${provider.name}?`,
    text: "Esta acción no se puede deshacer.",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  await api.delete(`/providers/${provider.id}`);
  await getProviders();
}

async function abrirDetalle(provider) {
  const res = await api.get(`/providers/${provider.id}`);
  detalle.value = res.data.provider;
  showDetalle.value = true;
}

async function agregarServicio() {
  if (!nuevoServicio.value.name.trim()) return;

  try {
    await api.post(`/providers/${detalle.value.id}/services`, nuevoServicio.value);
    nuevoServicio.value = { name: "", price: "" };
    const res = await api.get(`/providers/${detalle.value.id}`);
    detalle.value = res.data.provider;
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo agregar el servicio", text: err.response?.data?.error || "Error de conexión." });
  }
}

async function eliminarServicio(service) {
  await api.delete(`/providers/${detalle.value.id}/services/${service.id}`);
  detalle.value.services = detalle.value.services.filter(s => s.id !== service.id);
}

async function agregarContacto() {
  if (!nuevoContacto.value.name.trim()) return;

  try {
    await api.post(`/providers/${detalle.value.id}/contacts`, nuevoContacto.value);
    nuevoContacto.value = { name: "", phone: "", role: "" };
    const res = await api.get(`/providers/${detalle.value.id}`);
    detalle.value = res.data.provider;
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo agregar el contacto", text: err.response?.data?.error || "Error de conexión." });
  }
}

async function eliminarContacto(contact) {
  await api.delete(`/providers/${detalle.value.id}/contacts/${contact.id}`);
  detalle.value.contacts = detalle.value.contacts.filter(c => c.id !== contact.id);
}

function formatCOP(valor) {
  if (!valor) return "—";
  return Number(valor).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 });
}

onMounted(async () => {
  cargando.value = true;
  await getProviders();
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0" style="color: white;">
        <i class="bi bi-truck me-2"></i>
        Proveedores
      </h4>

      <argon-button color="success" @click="abrirCrear">
        <i class="bi bi-plus-circle me-1"></i>
        Nuevo proveedor
      </argon-button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="card">
      <div class="card-body table-responsive">
        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Email</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in providers" :key="p.id">
              <td>{{ p.name }}</td>
              <td>{{ p.phone || "—" }}</td>
              <td>{{ p.email || "—" }}</td>
              <td><span class="badge" :class="p.active ? 'bg-success' : 'bg-gradient-secondary'">{{ p.active ? "Activo" : "Inactivo" }}</span></td>
              <td class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-primary" @click="abrirDetalle(p)"><i class="bi bi-list-ul"></i> Servicios/Contactos</button>
                <button class="btn btn-sm btn-outline-secondary" @click="abrirEditar(p)"><i class="bi bi-pencil"></i></button>
                <button class="btn btn-sm btn-outline-danger" @click="eliminarProvider(p)"><i class="bi bi-trash"></i></button>
              </td>
            </tr>
            <tr v-if="providers.length === 0">
              <td colspan="5" class="text-center text-muted py-4">Todavía no hay proveedores registrados.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5>{{ isEdit ? "Editar proveedor" : "Nuevo proveedor" }}</h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label>Nombre</label>
                <argon-input v-model="form.name" />
              </div>
              <div class="col-md-6">
                <label>Teléfono</label>
                <argon-input v-model="form.phone" />
              </div>
              <div class="col-md-6">
                <label>Email</label>
                <argon-input v-model="form.email" />
              </div>
              <div class="col-12">
                <label>Notas</label>
                <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarProvider">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showModal" class="modal-backdrop fade show"></div>

    <!-- MODAL SERVICIOS / CONTACTOS -->
    <div v-if="showDetalle" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5>{{ detalle?.name }} — Servicios y contactos</h5>
            <button class="btn-close" @click="showDetalle = false"></button>
          </div>
          <div class="modal-body">
            <h6>Servicios ofrecidos</h6>
            <ul class="list-group mb-3">
              <li v-for="s in detalle?.services" :key="s.id" class="list-group-item d-flex justify-content-between align-items-center">
                <span>{{ s.name }} <span class="text-muted small">— {{ formatCOP(s.price) }}</span></span>
                <button class="btn btn-sm btn-outline-danger" @click="eliminarServicio(s)"><i class="bi bi-trash"></i></button>
              </li>
              <li v-if="!detalle?.services?.length" class="list-group-item text-muted small">Sin servicios cargados.</li>
            </ul>
            <div class="row g-2 mb-4">
              <div class="col-md-6"><argon-input v-model="nuevoServicio.name" placeholder="Nombre del servicio" /></div>
              <div class="col-md-4"><argon-input v-model="nuevoServicio.price" type="number" placeholder="Precio" /></div>
              <div class="col-md-2"><argon-button color="success" class="w-100" @click="agregarServicio">Agregar</argon-button></div>
            </div>

            <h6>Contactos / encargados</h6>
            <ul class="list-group mb-3">
              <li v-for="c in detalle?.contacts" :key="c.id" class="list-group-item d-flex justify-content-between align-items-center">
                <span>{{ c.name }} <span class="text-muted small">— {{ c.role || "Contacto" }} · {{ c.phone || "sin teléfono" }}</span></span>
                <button class="btn btn-sm btn-outline-danger" @click="eliminarContacto(c)"><i class="bi bi-trash"></i></button>
              </li>
              <li v-if="!detalle?.contacts?.length" class="list-group-item text-muted small">Sin contactos cargados.</li>
            </ul>
            <div class="row g-2">
              <div class="col-md-4"><argon-input v-model="nuevoContacto.name" placeholder="Nombre" /></div>
              <div class="col-md-3"><argon-input v-model="nuevoContacto.phone" placeholder="Teléfono" /></div>
              <div class="col-md-3"><argon-input v-model="nuevoContacto.role" placeholder="Rol, ej. Ventas" /></div>
              <div class="col-md-2"><argon-button color="success" class="w-100" @click="agregarContacto">Agregar</argon-button></div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showDetalle = false">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDetalle" class="modal-backdrop fade show"></div>

  </div>
</template>
